import { useEffect, useState, useContext } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Preloader from "../../shared/PreLoaders";
import "./Cashbook.css";
import {
  fetchCircles,
  fetchDivisionsByCircle,
  fetchRangesByDivision,
  fetchVfcByRange,
  fetchTransactions,
  fetchLoansByVfcAndDate,
  fetchLoansCollectionByVfc,
  fetchOpeningBalance,
  formatDate,
  handleEndOfBusiness,
} from "./CahbookFunction";
import { AuthContext } from "../../Context/AuthContext";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const Cashbook = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [circleData, setCircleData] = useState([]);
  const [divisionData, setDivisionData] = useState([]);
  const [rangeData, setRangeData] = useState([]);
  const [vfcData, setVfcData] = useState([]);

  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfcId, setSelectedVfcId] = useState("");
  const [selectDate, setSelectDate] = useState(null);

  const [incomeTransactions, setIncomeTransactions] = useState([]);
  const [expenseTransactions, setExpenseTransactions] = useState([]);
  const [openingBalance, setOpeningBalance] = useState({ cash: 0, bank: 0 });
  const [localOpeningBalance, setLocalOpeningBalance] = useState({ cash: 0, bank: 0 });

  const [closingBalance, setClosingBalance] = useState({ cash: 0, bank: 0 });
  const [incomeCashTotal, setIncomeCashTotal] = useState(0);
  const [incomeBankTotal, setIncomeBankTotal] = useState(0);
  const [expenseCashTotal, setExpenseCashTotal] = useState(0);
  const [expenseBankTotal, setExpenseBankTotal] = useState(0);
  const [loanRepaymentCashTotal, setLoanRepaymentCashTotal] = useState(0);
  const [loanRepaymentBankTotal, setLoanRepaymentBankTotal] = useState(0);
  const [loanCashTotal, setLoanCashTotal] = useState(0);
  const [loanBankTotal, setLoanBankTotal] = useState(0);

  const [loanData, setLoanData] = useState([]);
  const [loanDetails, setLoanDetails] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    fetchCircles(Api, setCircleData, setLoading);
  }, []);

  const handleViewCashbook = async () => {
    if (!selectedVfcId || !selectDate) {
      setError("Please select both VFC and Date");
      return;
    }
    setError(null);
    setLoading(true);
    const fetchedOpeningBalance = await fetchOpeningBalance(
      Api, selectedVfcId, selectDate, setOpeningBalance, setError, setLoading
    );
    setLocalOpeningBalance(fetchedOpeningBalance);
    console.log(fetchedOpeningBalance);

    await fetchTransactions(
      Api, selectedVfcId, selectDate,
      (data) => {
        const income = data.filter((txn) => txn.type === "R");
        const expense = data.filter((txn) => txn.type === "P");
        setIncomeTransactions(income);
        setExpenseTransactions(expense);

        const incomeCash = income.reduce((acc, txn) => (txn.cashOrBank === "cash" ? acc + txn.amount : acc), 0);
        const incomeBank = income.reduce((acc, txn) => (txn.cashOrBank === "bank" ? acc + txn.amount : acc), 0);
        const expenseCash = expense.reduce((acc, txn) => (txn.cashOrBank === "cash" ? acc + txn.amount : acc), 0);
        const expenseBank = expense.reduce((acc, txn) => (txn.cashOrBank === "bank" ? acc + txn.amount : acc), 0);

        setIncomeCashTotal(incomeCash);
        setIncomeBankTotal(incomeBank);
        setExpenseCashTotal(expenseCash);
        setExpenseBankTotal(expenseBank);
      },
      setError, setLoading
    );

    await fetchLoansByVfcAndDate(
      Api, selectedVfcId, selectDate,
      (loans) => {
        setLoanData(loans);
        const loanCashTotal = loans.reduce((acc, loan) => (loan.cashOrBank === "cash" ? acc + loan.amount : acc), 0);
        const loanBankTotal = loans.reduce((acc, loan) => (loan.cashOrBank === "bank" ? acc + loan.amount : acc), 0);
        setLoanCashTotal(loanCashTotal);
        setLoanBankTotal(loanBankTotal);
      },
      setLoading, setError
    );

    await fetchLoansCollectionByVfc(
      Api, selectedVfcId, selectDate,
      (repayments) => {
        setLoanDetails(repayments);
        const loanRepaymentCashTotal = repayments.reduce(
          (acc, repay) => repay.cashOrBank === "cash" ? acc + repay.amountPaid : acc, 0
        );
        const loanRepaymentBankTotal = repayments.reduce(
          (acc, repay) => repay.cashOrBank === "bank" ? acc + repay.amountPaid : acc, 0
        );
        setLoanRepaymentCashTotal(loanRepaymentCashTotal);
        setLoanRepaymentBankTotal(loanRepaymentBankTotal);
      },
      setLoading, setError
    );

    setClosingBalance({
      cash: fetchedOpeningBalance.cash + incomeCashTotal - expenseCashTotal - loanCashTotal + loanRepaymentCashTotal,
      bank: fetchedOpeningBalance.bank + incomeBankTotal - expenseBankTotal - loanBankTotal + loanRepaymentBankTotal,
    });
    setLoading(false);
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Cash Book</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>VFC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Cashbook</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select VFC & Date</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Circle</label>
                <select value={selectedCircle} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  onChange={(e) => {
                    const circleId = e.target.value;
                    setSelectedCircle(circleId);
                    fetchDivisionsByCircle(Api, circleId, setDivisionData, setLoading);
                  }}>
                  <option value="">Select Circle</option>
                  {circleData.map((circle) => (
                    <option key={circle._id} value={circle._id}>{circle.circleName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Division</label>
                <select value={selectedDivision} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  onChange={(e) => {
                    const divisionId = e.target.value;
                    setSelectedDivision(divisionId);
                    fetchRangesByDivision(Api, divisionId, setRangeData, setLoading);
                  }}>
                  <option value="">Select Division</option>
                  {divisionData.map((division) => (
                    <option key={division._id} value={division._id}>{division.divisionName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Range</label>
                <select value={selectedRange} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  onChange={(e) => {
                    const rangeId = e.target.value;
                    setSelectedRange(rangeId);
                    fetchVfcByRange(Api, rangeId, setVfcData, setLoading, selectedJfmcType?._id);
                  }}>
                  <option value="">Select Range</option>
                  {rangeData.map((range) => (
                    <option key={range._id} value={range._id}>{range.rangeName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>VFC</label>
                <select value={selectedVfcId} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  onChange={(e) => setSelectedVfcId(e.target.value)}>
                  <option value="">Select VFC</option>
                  {vfcData.map((vfc) => (
                    <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Date</label>
                <DatePicker
                  selected={selectDate}
                  onChange={(date) => setSelectDate(formatDate(date))}
                  dateFormat="dd/MM/yyyy"
                  customInput={<CustomInput />}
                />
              </div>
              <button type="button" onClick={handleViewCashbook}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
                <i className="mdi mdi-file-check" /> View Cashbook
              </button>
              <button type="button"
                onClick={() => handleEndOfBusiness(
                  Api, selectedVfcId, selectDate, closingBalance, setError, setLoading,
                  () => {
                    alert("End of Business recorded successfully!");
                    setIncomeTransactions([]);
                    setExpenseTransactions([]);
                    setOpeningBalance({ cash: 0, bank: 0 });
                    setClosingBalance({ cash: 0, bank: 0 });
                    setSelectDate(null);
                    setSelectedVfcId("");
                  }
                )}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#B71C1C,#E53E3E)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(229,62,62,0.3)", whiteSpace: "nowrap" }}>
                <i className="mdi mdi-upload" /> End of Business
              </button>
            </div>

            {error && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: 8, fontSize: 12, color: "#E53E3E", fontWeight: 600 }}>
                {error}
              </div>
            )}
          </div>

          {/* ── Cashbook Table ── */}
          {selectDate && selectedVfcId && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                  <i className="mdi mdi-cash-register" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Cashbook Records</span>
              </div>
              <div style={{ padding: 16, overflowX: "auto" }}>
                <div className="Table_data">
                  <table className="cashbook-table" style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #E2E8F0" }}>
                    <thead>
                      <tr style={{ background: "#F7FAFC" }}>
                        <th colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Sl.No</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Particulars</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Cash</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Bank</th>
                        <th colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Sl.No</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Particulars</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Cash</th>
                        <th style={{ border: "1px solid #E2E8F0", padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Bank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Opening Balance Row */}
                      <tr>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>1</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12, color: "#E53E3E" }}>Opening Balance</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12, color: "#E53E3E" }}>{localOpeningBalance.cash || openingBalance.cash}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12, color: "#E53E3E" }}>{localOpeningBalance.bank || openingBalance.bank}</td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>1</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                          {expenseTransactions.length > 0
                            ? expenseTransactions[0]?.voucherType?.voucherName || "N/A"
                            : "Opening Balance"}
                        </td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                          {expenseTransactions[0]?.cashOrBank === "cash" ? expenseTransactions[0]?.amount : ""}
                        </td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                          {expenseTransactions[0]?.cashOrBank === "bank" ? expenseTransactions[0]?.amount : ""}
                        </td>
                      </tr>

                      {/* Show Transactions + Loans Together */}
                      {Array.from({
                        length: Math.max(
                          incomeTransactions.length,
                          expenseTransactions.length,
                          loanData.length,
                          loanDetails.length
                        ),
                      }).map((_, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#F7FAFC" }}>
                          <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{i + 2}</td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {incomeTransactions[i]?.voucherType?.voucherName ||
                              (loanDetails[i]?.loanId ? "Loan Repayment" : "N/A")}
                          </td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {incomeTransactions[i]?.cashOrBank === "cash"
                              ? incomeTransactions[i]?.amount
                              : loanDetails[i]?.cashOrBank === "cash" ? loanDetails[i]?.amountPaid : ""}
                          </td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {incomeTransactions[i]?.cashOrBank === "bank"
                              ? incomeTransactions[i]?.amount
                              : loanDetails[i]?.cashOrBank === "bank" ? loanDetails[i]?.amountPaid : ""}
                          </td>
                          <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{i + 2}</td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {expenseTransactions[i]?.voucherType?.voucherName ||
                              (loanData[i]?.loanNo ? "Loan Disbursement" : "N/A")}
                          </td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {expenseTransactions[i]?.cashOrBank === "cash"
                              ? expenseTransactions[i]?.amount
                              : loanData[i]?.cashOrBank === "cash" ? loanData[i]?.amount : ""}
                          </td>
                          <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>
                            {expenseTransactions[i]?.cashOrBank === "bank"
                              ? expenseTransactions[i]?.amount
                              : loanData[i]?.cashOrBank === "bank" ? loanData[i]?.amount : ""}
                          </td>
                        </tr>
                      ))}

                      {/* Closing Balance */}
                      <tr style={{ color: "#E53E3E", fontWeight: 600 }}>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>Closing Balance</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{closingBalance.cash}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{closingBalance.bank}</td>
                      </tr>

                      {/* Totals */}
                      <tr style={{ fontWeight: 700, background: "#F0FFF4" }}>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>Total</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{incomeCashTotal + openingBalance.cash}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{incomeBankTotal + openingBalance.bank}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>Total</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{expenseCashTotal + closingBalance.cash}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{expenseBankTotal + closingBalance.bank}</td>
                      </tr>

                      {/* Income/Expense Total */}
                      <tr style={{ fontWeight: 700, background: "#F0FFF4" }}>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>Income Total</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{incomeCashTotal}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{incomeBankTotal}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}></td>
                        <td colSpan="2" style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>Expense Total</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{expenseCashTotal}</td>
                        <td style={{ border: "1px solid #E2E8F0", padding: "7px 10px", fontSize: 12 }}>{expenseBankTotal}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CustomInput = ({ value, onClick }) => (
  <button style={{ ...inputStyle, cursor: "pointer", textAlign: "left" }} onClick={onClick}>
    {value || "Select Date"}
  </button>
);

export default Cashbook;
